import { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast, Toaster } from 'sonner';
import { ShieldAlert, Trash2, Users, FileText, CreditCard, Loader2, RefreshCw, MessageSquare } from 'lucide-react';
import Seo from '@/components/Seo';

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wiping, setWiping] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/stats');
      setStats(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Failed to load admin stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === 'admin') load();
  }, [user]);

  // Wait for auth to resolve
  if (user === null) return null;
  // Not logged in or not admin → bounce to dashboard
  if (!user || user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const wipe = async () => {
    if (confirmText.trim().toUpperCase() !== 'WIPE') {
      toast.error('Type WIPE in capitals to confirm.');
      return;
    }
    setWiping(true);
    try {
      const { data } = await api.post('/admin/wipe-users');
      toast.success(
        `Wiped ${data.deleted_users} users, ${data.deleted_projects} quotes, ${data.deleted_payment_transactions} payment records.`
      );
      setConfirmText('');
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Wipe failed');
    } finally {
      setWiping(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-page">
      <Seo title="Admin" noindex />
      <Toaster richColors position="top-right" />

      <header className="mb-8 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-brand font-semibold">Admin · {user.email}</p>
          <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1">Control Room</h1>
          <p className="text-slate-600 mt-2">Visible only to <span className="font-semibold">admin</span> accounts. Use carefully — actions here are irreversible.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/admin/analytics" data-testid="admin-go-analytics" className="tm-btn tm-btn-dark">
            View Analytics →
          </Link>
          <button data-testid="admin-refresh" onClick={load} disabled={loading} className="tm-btn tm-btn-ghost">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''}/> Refresh
          </button>
        </div>
      </header>

      {loading && !stats ? (
        <div className="tm-card p-12 text-center">
          <Loader2 className="animate-spin mx-auto text-slate-900" size={32} />
          <p className="text-xs uppercase tracking-widest text-slate-500 mt-3 font-semibold">Loading stats…</p>
        </div>
      ) : stats ? (
        <>
          {/* Stat cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard testid="stat-total-users" icon={<Users size={20}/>} label="Total users" value={stats.total_users} sub={`${stats.non_admin_users} non-admin`} />
            <StatCard testid="stat-pro-users" icon={<CreditCard size={20}/>} label="Pro / Pro+" value={`${stats.pro_users + stats.pro_plus_users}`} sub={`${stats.pro_plus_users} on Pro+`} highlight />
            <StatCard testid="stat-projects" icon={<FileText size={20}/>} label="Saved quotes" value={stats.projects} />
            <StatCard testid="stat-feedback" icon={<MessageSquare size={20}/>} label="Feedback" value={stats.feedback} sub={`${stats.payment_transactions} txn records`} />
          </div>

          {/* Recent users */}
          <div className="tm-card p-6 mb-8">
            <h2 className="font-display font-bold text-xl uppercase tracking-tight mb-4">Recent sign-ups</h2>
            {stats.recent_users.length === 0 ? (
              <p className="font-mono text-sm text-slate-500">No non-admin users yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-slate-200">
                      <th className="py-2 pr-4 text-xs uppercase tracking-widest font-semibold">Email</th>
                      <th className="py-2 pr-4 text-xs uppercase tracking-widest font-semibold">Name</th>
                      <th className="py-2 pr-4 text-xs uppercase tracking-widest font-semibold">Tier</th>
                      <th className="py-2 pr-4 text-xs uppercase tracking-widest font-semibold">Status</th>
                      <th className="py-2 pr-4 text-xs uppercase tracking-widest font-semibold">Stripe</th>
                      <th className="py-2 text-xs uppercase tracking-widest font-semibold">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_users.map((u) => (
                      <tr key={u.id} className="border-b border-slate-200" data-testid={`admin-user-row-${u.email}`}>
                        <td className="py-2 pr-4">{u.email}</td>
                        <td className="py-2 pr-4 text-slate-600">{u.name || '—'}</td>
                        <td className="py-2 pr-4 uppercase text-xs font-semibold">
                          <span className={u.tier === 'pro_plus' ? 'tm-chip tm-chip-orange' : u.tier === 'pro' ? 'tm-chip' : 'text-slate-500'}>
                            {u.tier}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-slate-600">{u.status}</td>
                        <td className="py-2 pr-4 font-mono text-xs text-slate-500">{u.stripe_customer_id ? `${u.stripe_customer_id.slice(0, 12)}…` : '—'}</td>
                        <td className="py-2 text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString('en-GB') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Danger zone */}
          <div className="tm-card p-6 border border-red-600 bg-red-50">
            <div className="flex items-start gap-3 mb-4">
              <ShieldAlert className="text-red-600 shrink-0 mt-1" size={24} />
              <div>
                <h2 className="font-display font-semibold text-2xl uppercase tracking-tight text-red-800">Danger zone</h2>
                <p className="text-red-700 text-sm mt-1">
                  Permanently delete <strong>every non-admin user</strong> and all of their data (saved quotes, payment records, AI history). The admin account is preserved. Stripe customers stay in your Stripe dashboard — delete those separately if needed.
                </p>
              </div>
            </div>

            <div className="bg-white border border-red-600 p-4">
              <label className="block text-xs uppercase tracking-widest text-red-700 mb-1 font-semibold">
                Type <code className="bg-red-100 px-1">WIPE</code> to confirm
              </label>
              <input
                data-testid="admin-wipe-confirm-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="WIPE"
                className="tm-input mb-3 font-mono"
                autoComplete="off"
              />
              <button
                data-testid="admin-wipe-btn"
                onClick={wipe}
                disabled={wiping || confirmText.trim().toUpperCase() !== 'WIPE'}
                className="tm-btn bg-red-600 hover:bg-red-700 text-white border-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 size={16}/> {wiping ? 'Wiping…' : `Wipe ${stats.non_admin_users} non-admin user${stats.non_admin_users === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>

          <p className="mt-6 text-sm text-slate-500">
            <Link to="/dashboard" className="underline">← Back to dashboard</Link>
          </p>
        </>
      ) : null}
    </div>
  );
}

function StatCard({ icon, label, value, sub, highlight, testid }) {
  return (
    <div data-testid={testid} className={`tm-card p-5 ${highlight ? 'bg-slate-900 text-white' : ''}`}>
      <div className={`flex items-center gap-2 ${highlight ? 'text-brand' : 'text-slate-500'}`}>
        {icon}
        <p className="text-xs uppercase tracking-widest font-semibold">{label}</p>
      </div>
      <p className={`font-display font-semibold text-4xl mt-2 ${highlight ? 'text-white' : 'text-slate-900'}`}>{value}</p>
      {sub && <p className={`text-xs mt-1 ${highlight ? 'text-slate-300' : 'text-slate-500'}`}>{sub}</p>}
    </div>
  );
}
